"use client";

import { type PropsWithChildren, type ReactNode } from "react";

import { cn } from "../../utils/cn.util";
import { Accordion } from "../accordion";
import { ArrowPlainRightIcon } from "../icons";
import type { SvgIcon } from "../icons/type";
import { Popover } from "../popover";
import { Tooltip } from "../tooltip";
import { useLayoutContext } from "./context";
import SidebarItemLabel from "./SidebarItemLabel";

export interface LayoutSidebarBodyExpandItemProps extends PropsWithChildren {
  name: string;
  icon?: SvgIcon;
  isActive?: boolean;
  number?: ReactNode;
  className?: string;
}

const SidebarBodyExpandItem = ({
  className,
  name,
  icon: Icon,
  isActive,
  number,
  children,
}: LayoutSidebarBodyExpandItemProps) => {
  const { openSidebar } = useLayoutContext();

  return (
    <>
      {openSidebar ? (
        <Accordion.Root type="single" collapsible className="w-full" defaultValue={isActive ? "key" : ""}>
          <Accordion.Item value="key">
            <Accordion.Trigger
              className={cn(
                "group group/item flex h-10 w-full items-center gap-2.5 rounded-lg p-3 text-neutral-22 hover:bg-primary-98 hover:text-primary-40 [&[data-state=open]>svg]:rotate-90",
                !openSidebar && "pl-2.5",
                className
              )}
            >
              <SidebarItemLabel Icon={Icon} name={name} number={number} isActive={isActive} />
              <ArrowPlainRightIcon className="size-4 text-neutral-70 transition-all" />
            </Accordion.Trigger>
            <Accordion.Content className="flex flex-col gap-[2px] pl-6">{children}</Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      ) : (
        <Popover.Root>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Popover.Trigger
                className={cn(
                  "group group/item flex h-10! w-full! cursor-pointer items-center gap-2.5 rounded-lg p-3 text-neutral-22 hover:bg-primary-98 hover:text-primary-40",
                  !openSidebar && "pl-2.5",
                  isActive && "bg-primary-95! text-primary-40",
                  className
                )}
                onClick={e => e.stopPropagation()}
              >
                <SidebarItemLabel Icon={Icon} name={name} number={number} isActive={isActive} />
              </Popover.Trigger>
            </Tooltip.Trigger>
            <Tooltip.Content theme="light">
              {name}
              <Tooltip.Arrow theme="light"></Tooltip.Arrow>
            </Tooltip.Content>
          </Tooltip.Root>

          <Popover.Content align="start" side="right" sideOffset={16} className="flex w-full flex-col gap-1 p-2">
            {children}
          </Popover.Content>
        </Popover.Root>
      )}
    </>
  );
};

export default SidebarBodyExpandItem;
